import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { apiCallStatus } from 'helpers/constants';
import { Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { GeneralButton, GeneralSelect, Switch, TextArea } from 'sharedComponents';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { maxCommentSize } from './constants';
import { checkIfPreselectedCause, getEventCauseOptions } from './helpers';
import { causeOfEvents } from '../../../constants';


const SelfFaultEvaluationCommentActions = ({ model, actions }) => {
    const { t: translate } = useTranslation();
    const [causeOptions] = useState(getEventCauseOptions(translate, false));

    // Use Redux state for cause and preselected cause, with fallback to initial calculation
    const cause = model.selectedCause || checkIfPreselectedCause(model.selectedEvent?.rawEvent, causeOptions);
    const preSelectedCause = model.isPreselectedCause !== undefined ? model.isPreselectedCause : model.selectedEvent?.rawEvent?.isInitialCause;

    const [comment, setComment] = useState('');

    // Initialize cause in Redux if not already set or if event has changed
    useEffect(() => {
        const currentEventId = model.selectedEvent?.eventId;
        const hasEventChanged = model.currentEventId !== currentEventId;

        if (model.selectedEvent?.rawEvent && (!model.selectedCause || hasEventChanged)) {
            const initialCause = checkIfPreselectedCause(model.selectedEvent?.rawEvent, causeOptions);
            const initialPreselected = model.selectedEvent?.rawEvent?.isInitialCause || false;
            actions.initializeEventCauseState(initialCause, initialPreselected, currentEventId);
        }
    }, [model.selectedEvent?.rawEvent, model.selectedEvent?.eventId, model.selectedCause, model.currentEventId, causeOptions]);

    useEffect(() => {
        if (model.postCommentStatus === apiCallStatus.SUCCESS) {
            if (!preSelectedCause) {
                const unclassifiedCause = causeOptions.find(c => c.index === causeOfEvents.Unclassified);
                actions.setSelectedCause(unclassifiedCause);
            }
            setComment('');
        }
    }, [model.postCommentStatus, preSelectedCause, causeOptions]);

    const postComment = () => {
        actions.postComment(model.selectedEvent, comment, model.user.email, cause.index, preSelectedCause);
    };

    const eventCauseSelectHandler = (eventCause) => {
        actions.setSelectedCause(eventCause);
    };

    const handlePreselectedCauseChange = () => {
        actions.setPreselectedCause(!preSelectedCause);
    };

    return (
        <Col className='comments-actions' xl={5} xs={12}>
            <span className='self-evaluate-comment-label'>
                {translate('ABB.Powertrain.Frontend.commentLabelSelfEvaluate')}
            </span>
            <LoadingStatusWrapper loadingStatus={model.postCommentStatus} >
                <div className='select-cause-container'>
                    <span className='select-cause-label'>
                        {translate('ABB.Powertrain.Frontend.selectCauseLabelSelfEvaluate')}
                    </span>
                    <GeneralSelect
                        className='select-cause'
                        options={causeOptions}
                        value={cause}
                        onChange={eventCauseSelectHandler}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.index}
                        menuShouldScrollIntoView={false}
                        isSearchable={false}
                    />
                </div>
                <div className='preselection-container'>
                    <Switch
                        className='preselection-switch'
                        onChange={handlePreselectedCauseChange}
                        checked={preSelectedCause}
                    />
                    <span className='preselection-label'>{translate('ABB.Powertrain.Frontend.preselectionLabelSelfEvaluate')}</span>
                </div>
                <div className='event-comment'>
                    <div className='comment-wrapper'>
                        <div className='comment-label'>
                            {translate('ABB.Powertrain.Frontend.textareaLabelSelfEvaluate')}
                        </div>
                        <div className='comment-length'>{`${comment.length} / 500`}</div>
                    </div>
                    <TextArea
                        className='action-textarea'
                        resizable={false}
                        maxLength={maxCommentSize}
                        onChange={(e) => setComment(e)}
                        value={comment}
                        placeholder={translate(
                            'ABB.Powertrain.Frontend.textareaPlaceholderSelfEvaluate'
                        )}
                    />
                </div>
                <div className='action-button-container'>
                    <GeneralButton
                        disabled={!comment.length}
                        type='normal'
                        className='action-button'
                        text={translate('ABB.Powertrain.Frontend.postButtonSelfEvaluate')}
                        onClick={postComment}
                    />
                </div>
            </LoadingStatusWrapper>
        </Col>
    );
};

SelfFaultEvaluationCommentActions.propTypes = {
    actions: PropTypes.shape({
        postComment: PropTypes.func, // Function to post a comment
        setSelectedCause: PropTypes.func, // Function to set the selected cause
        setPreselectedCause: PropTypes.func, // Function to set the preselected cause flag
        initializeEventCauseState: PropTypes.func, // Function to initialize both cause and preselected flag
    }), // Actions object containing functions
    model: PropTypes.shape({
        selectedEvent: PropTypes.shape({
            id: PropTypes.string, // Event ID
            rawEvent: PropTypes.shape({
                isInitialCause: PropTypes.bool, // Whether the event is the initial cause
            }), // Raw event details
        }), // Selected event details
        postCommentStatus: PropTypes.string, // Status of the post comment API call
        user: PropTypes.shape({
            email: PropTypes.string, // User's email address
        }), // User details
        selectedCause: PropTypes.object, // Currently selected cause from Redux state
        isPreselectedCause: PropTypes.bool, // Whether the cause is preselected from Redux state
        currentEventId: PropTypes.string, // Current event ID to track event changes
    }), // Model containing state data
};

export default SelfFaultEvaluationCommentActions;
